<script setup lang="ts">
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import type {
  MinersStatusChartConf,
  PartsStatusChartConf,
  ServicesChartConf,
} from '@/components/rc/work-order-dashboard/charts-helpers'
import DashboardCharts from '@/components/rc/work-order-dashboard/DashboardCharts.vue'
import DashboardMinersTable from '@/components/rc/work-order-dashboard/DashboardMinersTable.vue'
import { useWorkOrderDetails } from '@/composables/rc/work-order-details'
import type { IWorkOrderDashboardMinersFilters } from '@/types/rc/filters'
import { MinerStatus } from '@/types/rc/miners/MinerStatus'
import { PartStatus } from '@/types/rc/miners/PartStatus'
import type { IService } from '@/types/rc/work-orders'
import { computed, reactive } from 'vue'

const props = defineProps<{ number: string }>()

const { data: workOrderDetails, isError, error, isPending } = useWorkOrderDetails(props.number)

const scopeOfWorkServices = computed<IService[]>(() => workOrderDetails.value?.scope_of_work ?? [])

const tableFilters = reactive<Partial<IWorkOrderDashboardMinersFilters>>({})

const clearFilters = () => {
  Object.keys(tableFilters).forEach((k) => delete tableFilters[k as keyof IWorkOrderDashboardMinersFilters])
}

// Chart click handlers:

const chartStatusLabelToFilterValue: Record<MinersStatusChartConf['category'], string> = {
  Cleaned: MinerStatus.Cleaned,
  'Initial Diagnostic': MinerStatus.DiagnosticPassed,
  'Stress Tested': MinerStatus.Completed, // Positively stress tested means 'Completed'
  Inventoried: MinerStatus.Inventoried,
} as const

const setMinerStatus = (status: MinersStatusChartConf['label'], category: MinersStatusChartConf['category']) => {
  clearFilters()
  if (status == 'Good') {
    tableFilters.miner_status = chartStatusLabelToFilterValue[category]
  } else if (status == 'Fail') {
    // Negative filters
    if (category == 'Stress Tested') tableFilters.miner_status = MinerStatus.FailedStressTest
    if (category == 'Initial Diagnostic') tableFilters.miner_status = MinerStatus.DiagnosticFailed
  } else {
    // If clicking any unset, just clear the filter
    tableFilters.miner_status = undefined
  }
}

// Hyphenated ones are custom for this view, supported by the BE
const chartPartStatusToFilterValue: Record<PartsStatusChartConf['category'], string | undefined> = {
  Good: PartStatus.Good,
  'Repaired/Replaced': 'Repair-Replaced',
  Fail: PartStatus.Fail,
  Blocked: PartStatus.Blocked,
  'Repair not authorized': PartStatus.RepairNotAuthorized,
  'Unrepairable/Irreparable': 'Unrepairable-Irrepairable',
  Undiagnosed: 'Undiagnosed',
}

const chartPartNameToFilterField = {
  PSU: 'psu_status',
  CB: 'cb_status',
  Fans: 'fan_status',
  HB: 'hb_status',
} as const

const setPartStatus = (part: PartsStatusChartConf['label'], status: PartsStatusChartConf['category']) => {
  clearFilters()
  const targetField = chartPartNameToFilterField[part]
  const targetStatus = chartPartStatusToFilterValue[status]
  tableFilters[targetField] = targetStatus
}

const setMissingService = (service: ServicesChartConf['label'], status: ServicesChartConf['category']) => {
  clearFilters()
  if (status == 'Not performed') {
    tableFilters.missing_service = service
  }
}
</script>

<template>
  <RlErrorAlert v-if="isError" :error="error" />
  <div v-if="!isError" v-loading="isPending && !isError" class="flex flex-col gap-6">
    <DashboardCharts
      :number="number"
      @set-miner-status="setMinerStatus"
      @set-part-status="setPartStatus"
      @set-missing-service="setMissingService"
    />
    <DashboardMinersTable
      :number="number"
      :scope-of-work="scopeOfWorkServices"
      :preset-filters="tableFilters"
      @clear-filters="clearFilters"
    />
  </div>
</template>
